import * as O from 'effect/Option'
import { PublicodesExpression } from 'publicodes'

import { CodeCatégorieJuridique } from '@/domaine/CodeCatégorieJuridique'
import { toPublicodeDate } from '@/domaine/Date'
import { PublicodesAdapter } from '@/domaine/engine/PublicodesAdapter'
import { Entreprise, RègleIdentitéEntreprise } from '@/domaine/Entreprise'
import { DottedName } from '@/domaine/publicodes/DottedName'
import { Action } from '@/store/actions/actions'
import { omit } from '@/utils'
import { buildSituationFromObject } from '@/utils/publicodes/publicodes'

import { SituationPublicodes } from './rootReducer'

const SAVED_NAMESPACES = [
	'indépendant . profession libérale . réglementée . métier',

	'dirigeant . gérant minoritaire',
	'dirigeant . indépendant . PL . métier',
	'entreprise . activité',
	'entreprise . activité . nature',
	'entreprise . activités',
	'entreprise . catégorie juridique',
	'entreprise . date de création',
	'entreprise . salariés',
	'entreprise . exercice',
	'entreprise . exonérations',
	'entreprise . TVA',
	'entreprise . association non lucrative',
	'entreprise . imposition',
	'entreprise . SIREN',
	'entreprise . nom',
	'entreprise . associés',
	'établissement',
	'salarié . convention collective',
	// Titre restaurant
	'salarié . rémunération . frais professionnels . titres-restaurant . montant unitaire',
	'salarié . rémunération . frais professionnels . titres-restaurant . taux employeur',
	// Mutuelle
	'salarié . cotisations . prévoyances . santé',
] as Array<DottedName>

export function isCompanyDottedName(dottedName: DottedName) {
	return SAVED_NAMESPACES.some((namespace) => dottedName.startsWith(namespace))
}

export function companySituation(
	state: SituationPublicodes = {},
	action: Action
): SituationPublicodes {
	switch (action.type) {
		case 'ENREGISTRE_LA_RÉPONSE_À_LA_QUESTION':
			if (isCompanyDottedName(action.fieldName)) {
				return {
					...state,
					[action.fieldName]: PublicodesAdapter.encode(O.some(action.value)),
				}
			}
			break
		case 'SUPPRIME_LA_RÈGLE_DE_LA_SITUATION': {
			return omit({ ...state }, action.fieldName) as SituationPublicodes
		}
		case 'COMPANY::SET_EXISTING_COMPANY':
			return getCompanySituation(action.entreprise)
		case 'COMPANY::RESET':
			return {}
		case 'COMPANY::ADD_COMMUNE_DETAILS':
			return {
				...state,
				...buildSituationFromObject('établissement . commune', action.details),
			}
		case 'COMPANY::SET_BÉNÉFICE_TYPE':
			return {
				...state,
				'entreprise . imposition . IR . type de bénéfices': `'${
					action.bénéfice === 'BIC/BNC' ? 'BNC' : action.bénéfice
				}'`,
				...(action.bénéfice === 'BA' && {
					'entreprise . imposition . IR . type de bénéfices . BA possible':
						'oui',
				}),
				...(action.bénéfice === 'BIC/BNC' && {
					'entreprise . imposition . IR . type de bénéfices . BIC et BNC possibles':
						'oui',
				}),
			}

		case 'CONFIGURE_LA_SIMULATION':
		case 'RÉINITIALISE_LA_SIMULATION':
			return state['entreprise . SIREN'] ? state : {}
	}

	return state
}

function getCompanySituation(
	entreprise: Entreprise
): Record<RègleIdentitéEntreprise, PublicodesExpression> {
	return {
		'entreprise . date de création': toPublicodeDate(entreprise.dateDeCréation),
		'entreprise . code catégorie juridique': entreprise.codeCatégorieJuridique,
		'entreprise . catégorie juridique': `'${getCatégorieFromCode(
			entreprise.codeCatégorieJuridique
		)}'`,
		'entreprise . SIREN': `'${entreprise.siren}'`,
		'entreprise . nom': `'${entreprise.nom}'`,
		'établissement . SIRET': `'${entreprise.établissement.siret}'`,
	}
}

type CatégorieJuridique = 'EI' | 'SARL' | 'SAS' | 'SELARL' | 'SELAS' | 'autre'

const getCatégorieFromCode = (
	code: CodeCatégorieJuridique
): CatégorieJuridique => {
	/*
	Nous utilisons le code entreprise pour connaitre le statut juridique
	(voir https://www.insee.fr/fr/information/2028129)

	En revanche, impossible de différencier EI et auto-entreprise
	https://www.insee.fr/fr/information/1730869
	*/

	if (code === '1000') {
		return 'EI'
	}
	if (code === '5485') {
		return 'SELARL'
	}
	if (code === '5470') {
		return 'autre'
	}
	if (/^54..$/.exec(code)) {
		return 'SARL'
	}
	if (code === '5785') {
		return 'SELAS'
	}
	if (code === '5710') {
		return 'SAS'
	}

	return 'autre'
}
